// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { FC, useContext, useEffect, useState } from "react";
import HTMLEllipsis from "react-lines-ellipsis/lib/html";
import { css } from "styled-components";

interface Props extends WidgetProps {}

export interface BrandDetailsContentStyles {
    container?: InjectableCss;
}

export const contentStyles: BrandDetailsContentStyles = {
    container: {
        css: css`
            margin: 30px 15px;
        `,
    },
};

const styles = contentStyles;

const BrandDetailsContent: FC<Props> = () => {
    const [useEllipsis, setUseEllipsis] = useState(true);
    const { value: brand } = useContext(BrandStateContext);
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (brand?.id) {
            dispatch({
                type: "xCoveo/setShouldSendSearchFromLinkEvent",
                shouldSendSearchFromLinkEvent: true,
            } as any);
        }

        return () => {
            dispatch({
                type: "xCoveo/setShouldSendSearchFromLinkEvent",
                shouldSendSearchFromLinkEvent: false,
            } as any);
        };
    }, [brand?.id]);

    if (!brand || !brand.htmlContent) {
        return null;
    }

    const handleTextClick = (e: any) => {
        const target = e.target;

        if (target.id === "BrandDetailsContent_readMore") {
            e.preventDefault();
            setUseEllipsis(false);
        }
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="brandContent" onClick={handleTextClick}>
            {useEllipsis && (
                <HTMLEllipsis
                    unsafeHTML={brand.htmlContent}
                    maxLine="3"
                    ellipsisHTML="... <span id='BrandDetailsContent_readMore'>read more</span>"
                    basedOn="letters"
                />
            )}
            {!useEllipsis && parse(transformContentHtml(brand.htmlContent), parserOptions)}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: BrandDetailsContent,
    definition: {
        group: "Brand Details",
        displayName: "Content",
        allowedContexts: ["BrandDetailsPage", "ProductListPage"],
    },
};

export default widgetModule;
